"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@utils/cn";
import { CircleDashed } from "lucide-react";
export interface ButtonProps extends React.ComponentPropsWithRef<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
  iconOnly?: boolean;
  loading?: boolean;
  buttonLabel?: string;
  /** Stable selector for debug capture and E2E tests. */
  testId?: string;
  iconPosition?: "start" | "end";
}
const buttonVariants = cva("flex items-center justify-center gap-x-1 py-1.5 px-5 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed", {
  variants: {
    variant: {
      filled: "enabled:hover:brightness-[85%] text-white shadow-md",
      outline: "",
      ghost: "",
      text: "bg-transparent hover:shadow-[0px_0px_5px_0px_hsl(var(--overlay)/0.12)] shadow-none text-primary"
    },
    typeColor: {
      primary: "bg-primary ",
      secondary: "bg-secondary",
      danger: "bg-danger",
      warning: "bg-warning",
      success: "bg-success"
    },
    size: {
      default: "w-fit",
      small: "font-medium text-sm rounded-sm w-fit",
      medium: "text-md font-semibold rounded-md w-3/4",
      large: "text-lg font-bold rounded-lg w-full"
    }
  },
  compoundVariants: [
  // outline
  {
    variant: "outline",
    typeColor: "primary",
    class: "text-primary bg-transparent enabled:hover:bg-primary-50 border-primary border"
  }, {
    variant: "outline",
    typeColor: "secondary",
    class: "text-secondary bg-transparent enabled:hover:bg-secondary-ghost border-secondary border"
  }, {
    variant: "outline",
    typeColor: "danger",
    class: "text-danger bg-transparent enabled:hover:bg-danger-50 border-danger border"
  }, {
    variant: "outline",
    typeColor: "warning",
    class: "text-warning bg-transparent enabled:hover:bg-warning-50 border-warning border"
  }, {
    variant: "outline",
    typeColor: "success",
    class: "text-success bg-transparent enabled:hover:bg-success/15 border-success border"
  },
  // ghost
  {
    variant: "ghost",
    typeColor: "primary",
    class: "text-primary bg-primary-50 shadow-none enabled:hover:bg-primary-200"
  }, {
    variant: "ghost",
    typeColor: "secondary",
    class: "text-secondary bg-secondary-ghost shadow-none enabled:hover:bg-[hsl(var(--secondary)/0.10)]"
  }, {
    variant: "ghost",
    typeColor: "danger",
    class: "text-danger bg-danger-50 shadow-none enabled:hover:bg-danger-200"
  }, {
    variant: "ghost",
    typeColor: "warning",
    class: "text-warning bg-warning-50 shadow-none enabled:hover:bg-warning-200"
  }, {
    variant: "ghost",
    typeColor: "success",
    class: "text-success bg-success/10 shadow-none enabled:hover:bg-success/20"
  },
  // text
  {
    variant: "text",
    typeColor: "primary",
    class: "text-primary bg-transparent enabled:hover:shadow-[0px_0px_5px_0px_hsl(var(--overlay)/0.12)] shadow-none"
  }, {
    variant: "text",
    typeColor: "secondary",
    class: "text-secondary bg-transparent enabled:hover:shadow-[0px_0px_5px_0px_hsl(var(--overlay)/0.12)] shadow-none"
  }, {
    variant: "text",
    typeColor: "danger",
    class: "text-danger bg-transparent enabled:hover:shadow-[0px_0px_5px_0px_hsl(var(--overlay)/0.12)] shadow-none"
  }, {
    variant: "text",
    typeColor: "warning",
    class: "text-warning bg-transparent enabled:hover:shadow-[0px_0px_5px_0px_hsl(var(--overlay)/0.12)] shadow-none"
  }, {
    variant: "text",
    typeColor: "success",
    class: "text-success bg-transparent enabled:hover:shadow-[0px_0px_5px_0px_hsl(var(--overlay)/0.12)] shadow-none"
  }],
  defaultVariants: {
    variant: "filled",
    size: "default",
    typeColor: "primary"
  }
});
const Button = t0 => {
  const $ = _c(48);
  let ariaLabel;
  let buttonLabel;
  let className;
  let disabled;
  let icon;
  let iconOnly;
  let loading;
  let props;
  let ref;
  let size;
  let t1;
  let t2;
  let testId;
  let typeColor;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      icon,
      loading,
      typeColor,
      asChild: t1,
      buttonLabel,
      testId,
      disabled,
      iconOnly,
      iconPosition: t2,
      ref,
      "aria-label": ariaLabel,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = buttonLabel;
    $[3] = className;
    $[4] = disabled;
    $[5] = icon;
    $[6] = iconOnly;
    $[7] = loading;
    $[8] = props;
    $[9] = ref;
    $[10] = size;
    $[11] = t1;
    $[12] = t2;
    $[13] = testId;
    $[14] = typeColor;
    $[15] = variant;
  } else {
    ariaLabel = $[1];
    buttonLabel = $[2];
    className = $[3];
    disabled = $[4];
    icon = $[5];
    iconOnly = $[6];
    loading = $[7];
    props = $[8];
    ref = $[9];
    size = $[10];
    t1 = $[11];
    t2 = $[12];
    testId = $[13];
    typeColor = $[14];
    variant = $[15];
  }
  const asChild = t1 === undefined ? false : t1;
  const iconPosition = t2 === undefined ? "start" : t2;
  const Comp = asChild ? Slot : "button";
  const resolvedAriaLabel = ariaLabel ?? (iconOnly && buttonLabel ? buttonLabel : undefined);
  const t3 = iconPosition === "start" && icon;
  let t4;
  if ($[16] !== buttonLabel || $[17] !== iconOnly) {
    t4 = !iconOnly && buttonLabel ? <span className="p-0 m-0">{buttonLabel}</span> : null;
    $[16] = buttonLabel;
    $[17] = iconOnly;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== icon || $[20] !== iconPosition || $[21] !== loading) {
    t5 = loading ? <CircleDashed className="mt-[2px] size-[15px] animate-spin" /> : iconPosition === "end" && icon;
    $[19] = icon;
    $[20] = iconPosition;
    $[21] = loading;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t6 = <>{t3}{t4}{t5}</>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const content = t6;
  let t7;
  if ($[27] !== className || $[28] !== iconOnly || $[29] !== size || $[30] !== typeColor || $[31] !== variant) {
    t7 = cn(buttonVariants({
      variant,
      size,
      typeColor
    }), iconOnly && "p-1", className);
    $[27] = className;
    $[28] = iconOnly;
    $[29] = size;
    $[30] = typeColor;
    $[31] = variant;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const t8 = loading && 0.8;
  let t9;
  if ($[33] !== props.style) {
    t9 = props.style || {};
    $[33] = props.style;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== t8 || $[36] !== t9) {
    t10 = {
      opacity: t8,
      ...t9
    };
    $[35] = t8;
    $[36] = t9;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const t11 = t10 as React.CSSProperties;
  const t12 = disabled || loading;
  const t13 = asChild ? props.children : content;
  let t14;
  if ($[38] !== Comp || $[39] !== props || $[40] !== ref || $[41] !== resolvedAriaLabel || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t7 || $[46] !== testId) {
    t14 = <Comp className={t7} ref={ref} style={t11} {...props} disabled={t12} data-testid={testId} aria-label={resolvedAriaLabel}>{t13}</Comp>;
    $[38] = Comp;
    $[39] = props;
    $[40] = ref;
    $[41] = resolvedAriaLabel;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t7;
    $[46] = testId;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};
Button.displayName = "Button";
export { Button, buttonVariants };